'use client';
import { MousePointer2, PenTool, ZoomIn, ZoomOut, Hand, GripVertical, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

export type Tool = 'select' | 'draw' | 'zoomIn' | 'zoomOut' | 'pan';
export type ToolbarProps = {
  selected?: Omit<Tool, 'zoomIn' | 'zoomOut'>;
  onClickZoomIn: () => void;
  onClickZoomOut: () => void;
  onClickPan: () => void;
  onClickDraw: () => void;
  onClickSelect: () => void;
  onClickDelete: () => void;
  zoomLevel: number;
  enableDelete: boolean;
  enableZoom: boolean;
};

export function Toolbar({
  selected = 'select',
  onClickSelect,
  onClickDraw,
  onClickPan,
  onClickZoomIn,
  onClickZoomOut,
  onClickDelete,
  zoomLevel,
  enableZoom,
  enableDelete,
}: ToolbarProps) {
  const [position, setPosition] = useState<{ x: number; y: number } | undefined>();
  const [isDragging, setIsDragging] = useState(false);

  const handleMouseDown = (event: React.MouseEvent) => {
    if (event.button !== 0) return;
    if (event.target instanceof HTMLElement && event.target.id !== 'drag-handle') return;
    setIsDragging(true);
    event.preventDefault();
  };

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (!isDragging) return;

      setPosition((prev = { x: 0, y: 0 }) => ({
        x: prev.x + event.movementX,
        y: prev.y + event.movementY,
      }));
    },
    [isDragging],
  );

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    } else {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove]);

  // Style for positioning
  const style = position && {
    transform: `translate(${position.x}px, ${position.y}px)`,
  };

  return (
    <div
      onMouseDown={handleMouseDown}
      style={style}
      className="absolute bottom-5 left-1/2 bg-background/70 transform -translate-x-1/2 p-2 backdrop-blur-sm rounded-xl shadow-lg cursor-default"
    >
      <TooltipProvider>
        <Card className="shadow-none bg-transparent border-0 h-full">
          <div className="flex items-center gap-2">
            <div className="handle cursor-move p-1 rounded" id="drag-handle">
              <GripVertical className="h-8 w-6" />
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={selected === 'select' ? 'default' : 'ghost'}
                  size="icon"
                  onClick={onClickSelect}
                >
                  <MousePointer2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Select (S)</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={selected === 'draw' ? 'default' : 'ghost'}
                  size="icon"
                  onClick={onClickDraw}
                >
                  <PenTool className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Pen (P)</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={selected === 'pan' ? 'default' : 'ghost'}
                  size="icon"
                  onClick={onClickPan}
                  disabled={false}
                >
                  <Hand className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Pan (H)</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClickDelete}
                  disabled={!enableDelete}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Delete (Delete)</p>
              </TooltipContent>
            </Tooltip>
            <Separator orientation="vertical" className="mx-2 bg-gray-700 h-[14px]" />
            <div className="flex items-center gap-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    disabled={!enableZoom}
                    variant="ghost"
                    size="icon"
                    onClick={onClickZoomOut}
                    aria-label="Zoom out"
                  >
                    <ZoomOut className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Zoom Out (-)</p>
                </TooltipContent>
              </Tooltip>
              <div className="text-xs font-medium">{zoomLevel}%</div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    disabled={!enableZoom}
                    variant="ghost"
                    size="icon"
                    onClick={onClickZoomIn}
                    aria-label="Zoom in"
                  >
                    <ZoomIn className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Zoom In (+)</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        </Card>
      </TooltipProvider>
    </div>
  );
}
